// The first payment question asked when a build is submitted (Oct 21, 2026).
// See src/lib/buildPaymentPlan.ts for the rule and where it's asked.
//
// The picker is presentational: it shows what's recorded on the deal, the four
// plans when the first payment isn't captured, the reason box (required for
// Other) and Greg's commission note. useBuildPaymentState loads the full deal
// row and the plan already on file; usePlanChoice seeds the choice from it.

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { supabase } from "../../lib/supabase";
import {
  COMMISSION_NOTE, PAYMENT_PLAN_HINTS, PAYMENT_PLAN_KEYS, PAYMENT_PLAN_LABELS,
  fetchCurrentPlan, firstPaymentStatus, planNeedsNote, usd,
  type BuildPaymentPlan, type FirstPaymentStatus, type PlanChoice,
} from "../../lib/buildPaymentPlan";

export interface BuildPaymentSnapshot {
  deal: Record<string, unknown>;
  status: FirstPaymentStatus;
  onFile: BuildPaymentPlan | null;
  /** Who recorded the plan on file. */
  onFileBy: string | null;
}

/** The full deal row (scope numbers and payments) and the plan on file. */
export async function loadBuildPaymentSnapshot(dealId: string): Promise<BuildPaymentSnapshot> {
  const [{ data, error }, onFile] = await Promise.all([
    supabase.from("deals").select("*").eq("id", dealId).maybeSingle(),
    fetchCurrentPlan(dealId),
  ]);
  if (error) throw error;
  if (!data) throw new Error("Couldn't load this deal.");
  let onFileBy: string | null = null;
  if (onFile?.actor_id) {
    const { data: who } = await supabase.from("team_roster").select("full_name").eq("id", onFile.actor_id).maybeSingle();
    onFileBy = (who?.full_name as string | undefined) ?? null;
  }
  const deal = data as Record<string, unknown>;
  return { deal, status: firstPaymentStatus(deal), onFile, onFileBy };
}

export function useBuildPaymentState(dealId: string, enabled: boolean) {
  const [snap, setSnap] = useState<BuildPaymentSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    setLoading(true);
    setError(null);
    loadBuildPaymentSnapshot(dealId)
      .then((s) => { if (alive) setSnap(s); })
      .catch((e) => { if (alive) setError((e as Error).message || "Couldn't load the payments."); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [dealId, enabled, tick]);
  return { snap, error, loading, reload };
}

/** The choice, seeded once from the plan on file. */
export function usePlanChoice(snap: BuildPaymentSnapshot | null) {
  const [choice, setChoice] = useState<PlanChoice>({ plan: "", note: "" });
  const seeded = useRef<string | null>(null);
  useEffect(() => {
    if (!snap) return;
    const key = snap.onFile?.id ?? "none";
    if (seeded.current === key) return;
    seeded.current = key;
    if (snap.onFile) setChoice({ plan: snap.onFile.plan, note: snap.onFile.note ?? "" });
  }, [snap]);
  return [choice, setChoice] as const;
}

/** A plan is needed: the first payment isn't captured, or the server said so. */
export function planNeeded(snap: BuildPaymentSnapshot | null, forcePlan = false): boolean {
  return forcePlan || !snap || !snap.status.isCaptured;
}

function fmtWhen(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "America/New_York" });
}

interface Props {
  snap: BuildPaymentSnapshot | null;
  loading?: boolean;
  error?: string | null;
  value: PlanChoice;
  onChange: (c: PlanChoice) => void;
  /** The server didn't count the first payment as captured: ask anyway. */
  forcePlan?: boolean;
  compact?: boolean;
  /** A line above the question, e.g. why Robert is being asked. */
  intro?: ReactNode;
}

export default function BuildPaymentPlanPicker({ snap, loading, error, value, onChange, forcePlan = false, compact = false, intro }: Props) {
  const text = compact ? "text-[12px]" : "text-sm";
  const small = compact ? "text-[10px]" : "text-[11px]";
  const status = snap?.status ?? null;
  const title = status?.label === "50% deposit" ? "50% deposit" : "First payment";
  const recorded = status ? status.received + status.inFlight : 0;
  const ask = planNeeded(snap, forcePlan);
  const otherNote = planNeedsNote(value.plan);

  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden bg-white">
      <div className={`px-3 py-2 bg-slate-50 border-b border-slate-200 ${small} font-semibold uppercase tracking-wider text-slate-500`}>
        {title}
      </div>
      <div className={`px-3 py-2.5 ${text} space-y-2`}>
        {loading && !snap ? (
          <div className={`${small} text-slate-400`}>Loading the payments…</div>
        ) : error && !snap ? (
          <div className="text-red-700 text-xs">{error}</div>
        ) : !status ? null : !ask && status.isCleared ? (
          <div className="text-emerald-700">✓ Cleared: {usd(status.received)} in. No plan needed.</div>
        ) : !ask ? (
          <div className="text-emerald-700">
            ✓ Captured: {usd(recorded)} recorded, {usd(status.inFlight)} of it still clearing. No plan needed.
          </div>
        ) : (
          <>
            {intro && <div className={`${small} text-slate-500`}>{intro}</div>}
            <div className="text-slate-700">
              {forcePlan && status.isCaptured ? (
                <>The server doesn't count the {status.label} as in yet.</>
              ) : status.owed != null ? (
                <>
                  <span className="font-semibold">{usd(status.owed)}</span> not in yet
                  {recorded > 0 ? ` (${usd(recorded)} recorded so far)` : ""}.
                </>
              ) : (
                <>Not in yet (amount unknown: no scope entered).</>
              )}
              {" "}How will it be collected?
            </div>
            {snap?.onFile && (
              <div className={`${small} text-slate-500`}>
                On file: {PAYMENT_PLAN_LABELS[snap.onFile.plan]}
                {snap.onFile.note ? ` (${snap.onFile.note})` : ""} · {fmtWhen(snap.onFile.created_at)}
                {snap.onFileBy ? ` by ${snap.onFileBy}` : ""}. Change it if it's different now.
              </div>
            )}
            <div className={"grid gap-1.5 " + (compact ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2")}>
              {PAYMENT_PLAN_KEYS.map((k) => {
                const on = value.plan === k;
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => onChange({ ...value, plan: k })}
                    aria-pressed={on}
                    className={"text-left rounded-lg border px-3 py-2 " + small + " " + (on ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-700 hover:bg-slate-50")}
                  >
                    <span className={`font-semibold ${compact ? "text-[11px]" : "text-xs"}`}>{PAYMENT_PLAN_LABELS[k]}</span>
                    <span className={"block mt-0.5 " + (on ? "text-slate-300" : "text-slate-400")}>{PAYMENT_PLAN_HINTS[k]}</span>
                  </button>
                );
              })}
            </div>
            {value.plan && (
              <div>
                <label className={`block ${small} font-medium text-slate-600 mb-1`}>
                  {otherNote ? "Reason (required)" : "Note for Chad and Robert (optional)"}
                </label>
                <textarea
                  value={value.note}
                  onChange={(e) => onChange({ ...value, note: e.target.value })}
                  rows={2}
                  maxLength={500}
                  placeholder={otherNote ? "How the money will come in, and when" : "e.g. picking up the check Thursday"}
                  className={`w-full border rounded-lg px-2.5 py-1.5 ${compact ? "text-[12px]" : "text-sm"} ${otherNote && value.note.trim().length < 3 ? "border-amber-300" : "border-slate-200"}`}
                />
              </div>
            )}
            <p className={`${small} text-slate-400`}>If the check is already in hand, record it on the deal's Payments tab first and it counts as captured.</p>
          </>
        )}
        {status && !status.isCleared && (
          <div className={`flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 px-2.5 py-2 ${small} text-amber-900`}>
            <span aria-hidden>💵</span>
            <span>{COMMISSION_NOTE}</span>
          </div>
        )}
      </div>
    </div>
  );
}
